import { useKeyboardShortcut } from '@bestseller-bit/frontend-community.hooks.use-keyboard-shortcut';
import { datadogRum } from '@datadog/browser-rum';
import { Delete, Edit, VerifiedOutlined } from '@mui/icons-material';
import { IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { GridApi, GridReadyEvent, GroupCellRendererParams, IRowNode } from 'ag-grid-community';
import { groupBy } from 'lodash';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import Container from 'src/components/atoms/Container';
import { GroupedOption } from 'src/components/atoms/Select/Select';
import { CreateClusterDialog } from 'src/components/organisms/CreateClusterDialog/CreateClusterDialog';
import { DataTable } from 'src/components/organisms/DataTable/DataTable';
import { Cell } from 'src/components/organisms/DataTable/types';
import { DeleteDialog } from 'src/components/organisms/DeleteDialog/DeleteDialog';
import { TableWrapper } from 'src/components/styled/TableWrapper';
import { Module } from 'src/domain';
import { ClusterManagementUnsavedChanges } from 'src/domain/cluster-management/ClusterManagementUnsavedChanges';
import { ClusterManagementRow } from 'src/domain/models/cluster-management/cluster-management-row';
import { useChainId, useHasUnsavedChanges, useType, useUnsavedChangesModule } from 'src/hooks';
import { useCustomAgGridNavigation } from 'src/hooks/ag-grid/useCustomAgGridNavigation';
import { useClusterManagementSimulationRows } from 'src/hooks/cluster-management/simulation/useClusterManagementSimulationRows';
import { useDiscardChangesModule } from 'src/hooks/discard-changes/useDiscardChangesModule';
import { useCompositePartner } from 'src/hooks/partner/useCompositePartner';
import { useDiscardShortcut } from 'src/hooks/shortcuts/useDiscardShortcut';
import { useSaveShortcut } from 'src/hooks/shortcuts/useSaveShortcut';
import { useChangesSnackbar } from 'src/hooks/snackbar/useChangesSnackbar';
import { useSnackbar } from 'src/hooks/snackbar/useSnackbar';
import { useCurrentId } from 'src/hooks/useCurrentId';
import { UpdateClusterInput } from 'src/infrastructure/rest-api/api-types';
import { useApiMutation } from 'src/infrastructure/rest-api/useApi';
import { isHTMLElement } from 'src/utils/dom';
import { ClusterManagementTopBar } from './ClusterManagementTopBar';
import { defaultColDef, defaultColumns } from './data/columns';

// It seems ag grid removed this type export, so we'll just define what we need here
interface RowGroupingRowNode<T = unknown> extends IRowNode<T> {
    groupValue: T;
}

interface AgEditClusterEvent {
    clusterId: string;
}

export const ClusterManagementView: FC = () => {
    const { data: rows, clusters, loading } = useClusterManagementSimulationRows();
    const [createClusterDialogOpen, setCreateClusterDialogOpen] = useState(false);
    const hasUnsavedChanges = useHasUnsavedChanges(Module.ClusterManagement);
    const discardChanges = useDiscardChangesModule(Module.ClusterManagement);
    const gridApiRef = useRef<GridApi | null>(null);
    const [editingClusterId, setEditingClusterId] = useState<number | null>(null);
    const [deletingClusterId, setDeletingClusterId] = useState<string | null>(null);
    const showSnackbar = useSnackbar();
    const showChangesSnackbar = useChangesSnackbar();
    const type = useType();
    const currentId = useCurrentId();
    const chainId = useChainId();
    const compositePartner = useCompositePartner();

    const [saveClusterData, { loading: isSaving }] = useApiMutation('/api/clusters', 'put', {
        update(data, _variables, queryClient) {
            queryClient.setQueryData([`/api/clusters/partners/${compositePartner?.id}`, undefined], data);
        },
    });

    const [unsavedChanges, setUnsavedChanges] = useUnsavedChangesModule<ClusterManagementUnsavedChanges>(
        Module.ClusterManagement
    );

    const editingCluster = useMemo(() => {
        if (editingClusterId === null) {
            return null;
        }
        return clusters?.find((cluster) => cluster.clusterId === editingClusterId);
    }, [editingClusterId, clusters]);

    const deletingCluster = useMemo(() => {
        if (deletingClusterId === null) {
            return null;
        }
        return clusters?.find((cluster) => cluster.clusterId === Number(deletingClusterId));
    }, [deletingClusterId, clusters]);

    const deleteCluster = () => {
        // move all clusters to default cluster
        const defaultCluster = clusters?.find((cluster) => cluster.isDefault);

        if (!defaultCluster) {
            showSnackbar('Cannot delete cluster, there is no default cluster to move stores to.', 'error');
            setDeletingClusterId(null);
            return;
        }

        const updatedStores =
            rows?.map((row) => {
                if (row.cluster?.clusterId === deletingCluster?.clusterId) {
                    return {
                        ...row,
                        cluster: defaultCluster,
                    };
                }
                return row;
            }) ?? [];

        // remove cluster from list
        const updatedClusters = clusters?.filter((cluster) => cluster.clusterId !== deletingCluster?.clusterId) ?? [];

        // update unsaved changes
        setUnsavedChanges({
            clusters: updatedClusters,
            rows: updatedStores,
        });

        setDeletingClusterId(null);
    };

    useEffect(() => {
        const handleEditCluster = (event: CustomEventInit<AgEditClusterEvent>) => {
            setEditingClusterId(Number(event.detail!.clusterId));
            setCreateClusterDialogOpen(true);
        };

        const handleDeleteCluster = (event: CustomEventInit<AgEditClusterEvent>) => {
            setDeletingClusterId(event.detail!.clusterId);
        };

        globalThis.addEventListener('ag-edit-cluster', handleEditCluster);
        globalThis.addEventListener('ag-delete-cluster', handleDeleteCluster);

        return () => {
            globalThis.removeEventListener('ag-edit-cluster', handleEditCluster);
            globalThis.removeEventListener('ag-delete-cluster', handleDeleteCluster);
        };
    }, []);

    const tableWrapperRef = useRef<HTMLDivElement>(null);

    useCustomAgGridNavigation({
        api: gridApiRef.current,
        tableWrapperRef,
    });

    const handleCellValuesChanged = (cells: Cell[]) => {
        const newClusters = structuredClone(clusters ?? []);
        const newRows = structuredClone(rows ?? []);
        for (const cell of cells) {
            const row = newRows.find((r) => r.rowId === cell.rowId);
            const cellClusterId = typeof cell.value === 'object' ? Number(cell.value.clusterId) : Number(cell.value);
            const newCluster = newClusters?.find((cluster) => cluster.clusterId === cellClusterId);
            if (row && newCluster) {
                const oldCluster = newClusters.find((cluster) => cluster.clusterId === row.cluster?.clusterId);
                oldCluster?.stores.splice(oldCluster.stores.indexOf(row.storeId), 1);
                newCluster.stores.push(row.storeId);
                row.cluster = newCluster;
            }
        }

        setUnsavedChanges({
            ...unsavedChanges,
            rows: newRows,
        });
    };

    const handleGridReady = (event: GridReadyEvent) => {
        gridApiRef.current = event.api;
    };

    const handleExpandAll = () => {
        gridApiRef.current?.expandAll();
    };

    const handleCollapseAll = () => {
        gridApiRef.current?.collapseAll();
    };

    const cancelCreateClusterDialog = () => {
        setCreateClusterDialogOpen(false);
        setEditingClusterId(null);
    };

    const handleCreateCluster = (clusterName: string, clusterDescription: string = '') => {
        const clusterNameExists = rows.some((row) => row.cluster?.clusterName === clusterName);
        if (clusterNameExists && !(editingCluster?.clusterName === clusterName)) {
            showSnackbar(`Cluster with name "${clusterName}" already exists. Please choose a different name.`, {
                variant: 'error',
                preventDuplicate: true,
            });
            return;
        }

        if (editingCluster) {
            const unsavedClusters = clusters ?? [];
            const clusterToUpdate = unsavedClusters.find((cluster) => cluster.clusterId === editingCluster.clusterId);

            if (clusterToUpdate?.clusterName === clusterName && clusterToUpdate?.description === clusterDescription) {
                // no changes
                setEditingClusterId(null);
                setCreateClusterDialogOpen(false);
                return;
            }

            const index = unsavedClusters?.indexOf(clusterToUpdate!);
            unsavedClusters[index] = {
                ...clusterToUpdate!,
                clusterName,
                description: clusterDescription,
            };

            setUnsavedChanges({
                ...unsavedChanges,
                clusters: unsavedClusters,
            });

            setEditingClusterId(null);
        } else {
            const unsavedClusters = clusters ?? [];
            const newClusterId =
                -1 *
                (Math.max(...unsavedClusters.map((cluster) => cluster.clusterId ?? 0)) + unsavedClusters?.length + 1);

            unsavedClusters.push({
                clusterId: newClusterId,
                clusterName,
                description: clusterDescription,
                isDefault: false,
                stores: [],
            });
            setUnsavedChanges({
                ...unsavedChanges,
                clusters: unsavedClusters,
            });
        }
        setCreateClusterDialogOpen(false);
    };

    const saveChanges = () => {
        if (!rows || !clusters) {
            return;
        }

        const nonEmptyRows = rows.filter((row) => !row.rowId.startsWith('empty'));
        const storesByClusterId = groupBy(nonEmptyRows, (row) => row.cluster?.clusterId);
        const updatedClusters = Object.entries(storesByClusterId).reduce<UpdateClusterInput[]>(
            (acc, [clusterId, stores]) => {
                const cluster = clusters.find((c) => c.clusterId === Number(clusterId));

                if (!cluster) {
                    datadogRum.addError(new Error(`Cluster not found`), {
                        clusterId,
                        chainId,
                        compositePartnerId: compositePartner?.id,
                        groupType: type,
                        groupId: currentId,
                    });
                    return acc;
                }

                acc.push({
                    clusterId:
                        typeof cluster.clusterId === 'number' && cluster.clusterId >= 0 ? cluster.clusterId : null,
                    clusterName: cluster.clusterName,
                    description: cluster.description ?? '',
                    isDefault: cluster.isDefault,
                    stores: stores.map((store) => store.storeId),
                });

                return acc;
            },
            []
        );

        const emptyClusters = clusters
            .filter((cluster) => !storesByClusterId[cluster.clusterId!])
            .map((cluster) => ({
                clusterId: typeof cluster.clusterId === 'number' && cluster.clusterId >= 0 ? cluster.clusterId : null,
                clusterName: cluster.clusterName,
                description: cluster.description ?? '',
                isDefault: cluster.isDefault,
                stores: [],
            }));

        updatedClusters.push(...emptyClusters);

        saveClusterData({
            body: updatedClusters,
        })
            .then(() => {
                showChangesSnackbar();
                discardChanges();
            })
            .catch((err) => {
                showChangesSnackbar(err);
            });
    };

    const columns = useMemo(() => {
        return defaultColumns.map((column) => {
            if (column.field === 'cluster') {
                return {
                    ...column,
                    cellEditorParams: {
                        selectOptions: (clusters ?? []).map((cluster) => ({
                            display: cluster.clusterName,
                            value: cluster.clusterId!.toString(),
                            isHeader: false,
                        })) as GroupedOption[],
                    },
                };
            }

            return column;
        });
    }, [clusters]);

    useKeyboardShortcut({
        shortcutName: 'NEW',
        callback(event) {
            event.preventDefault();
            event.stopPropagation();
            setEditingClusterId(null);
            setCreateClusterDialogOpen(true);
        },
    });

    useKeyboardShortcut({
        shortcutName: 'EDIT/VIEW',
        callback(event) {
            event.preventDefault();
            event.stopPropagation();
            const selectedRows = gridApiRef.current?.getSelectedNodes();
            if (selectedRows?.length === 1) {
                if (isHTMLElement(document.activeElement)) {
                    // Blur the active element to prevent focus issues when opening the dialog
                    document.activeElement.blur();
                }

                const row = selectedRows[0];
                if (row.group) {
                    // Group row, open edit dialog for cluster
                    const data = (selectedRows[0] as RowGroupingRowNode).groupValue as {
                        clusterId: number;
                    };
                    setEditingClusterId(data.clusterId);
                    setCreateClusterDialogOpen(true);
                } else if (typeof row.rowIndex === 'number') {
                    // Store row, edit cluster column
                    gridApiRef.current?.startEditingCell({
                        rowIndex: row.rowIndex,
                        colKey: 'cluster',
                    });
                }
            }
        },
    });

    useKeyboardShortcut({
        shortcutName: 'EXPAND/COLLAPSE',
        callback: (event) => {
            event.preventDefault();
            event.stopPropagation();

            let isExpanded = true;
            gridApiRef.current?.forEachNodeAfterFilter((node) => {
                if (node.group && !node.expanded) {
                    isExpanded = false;
                }
            });

            if (isExpanded) {
                handleCollapseAll();
            } else {
                handleExpandAll();
            }
        },
    });

    useKeyboardShortcut({
        shortcutName: 'DELETE',
        callback(event) {
            event.preventDefault();
            event.stopPropagation();
            const selectedRows = gridApiRef.current?.getSelectedNodes();
            if (selectedRows?.length === 1 && selectedRows[0].group) {
                // biome-ignore lint/suspicious/noExplicitAny: groupValue is not exposed on the types, so we need to use any
                const data = (selectedRows[0] as any).groupValue as {
                    clusterId: number;
                };
                setDeletingClusterId(data.clusterId.toString());
            }
        },
    });

    useSaveShortcut(saveChanges, !hasUnsavedChanges);
    useDiscardShortcut(discardChanges, !hasUnsavedChanges);

    return (
        <>
            {createClusterDialogOpen && (
                <CreateClusterDialog
                    key={editingCluster ? `edit-${editingCluster.clusterId}` : undefined}
                    isEditing={!!editingCluster}
                    initialClusterName={editingCluster?.clusterName}
                    initialClusterDescription={editingCluster?.description}
                    open
                    onOk={handleCreateCluster}
                    onCancel={cancelCreateClusterDialog}
                />
            )}
            {!!deletingCluster && (
                <DeleteDialog
                    open
                    title="Delete cluster?"
                    content={`Are you sure you want to delete cluster "${deletingCluster?.clusterName}"?  Deleting is permanent and cannot be undone.`}
                    onCancel={() => setDeletingClusterId(null)}
                    onDelete={deleteCluster}
                />
            )}
            <Stack spacing={2}>
                <ClusterManagementTopBar
                    isDiscardButtonDisabled={!hasUnsavedChanges}
                    isSaveButtonDisabled={!hasUnsavedChanges}
                    isSaving={isSaving}
                    onAddCluster={() => setCreateClusterDialogOpen(true)}
                    onCollapseAll={handleCollapseAll}
                    onDiscardChanges={discardChanges}
                    onExpandAll={handleExpandAll}
                    onSaveChanges={saveChanges}
                />
                <Container>
                    <TableWrapper
                        sx={{ flex: 1 }}
                        className="ag-theme-alpine-custom-compact"
                        hasUnsavedChanges={hasUnsavedChanges}
                        ref={tableWrapperRef}
                    >
                        <DataTable
                            onCellValuesChanged={handleCellValuesChanged}
                            isNumberTable={false}
                            loading={loading}
                            onGridReady={handleGridReady}
                            columns={columns}
                            defaultColDef={defaultColDef}
                            rows={rows}
                            groupDisplayType="groupRows"
                            groupRowRendererParams={{
                                innerRenderer: ClusterManagementRowGroupCell,
                                suppressCount: true,
                            }}
                            rowSelection={{
                                mode: 'singleRow',
                                checkboxes: false,
                                enableClickSelection: true,
                            }}
                        ></DataTable>
                    </TableWrapper>
                </Container>
            </Stack>
        </>
    );
};

const ClusterManagementRowGroupCell: FC<GroupCellRendererParams<ClusterManagementRow>> = ({ node }) => {
    const childNode = node?.childrenAfterGroup?.first();
    const value = childNode?.data?.cluster;

    const onEdit = () => {
        if (!value?.clusterId) {
            return;
        }
        globalThis.dispatchEvent(
            new CustomEvent<AgEditClusterEvent>('ag-edit-cluster', {
                detail: {
                    clusterId: value.clusterId.toString(),
                },
            })
        );
    };

    const onDelete = () => {
        if (!value?.clusterId) {
            return;
        }
        globalThis.dispatchEvent(
            new CustomEvent<AgEditClusterEvent>('ag-delete-cluster', {
                detail: {
                    clusterId: value.clusterId.toString(),
                },
            })
        );
    };

    const storeCount = node.childrenAfterGroup?.filter((node) => !node.data?.rowId.startsWith('empty')).length;

    return (
        <Stack
            direction="row"
            sx={{
                alignItems: 'center',
                gap: '0.5rem',
                width: '100%',
            }}
        >
            <Stack
                direction="row"
                sx={{
                    gap: '0.5rem',
                    alignItems: 'center',
                }}
            >
                <Typography
                    sx={{
                        fontWeight: 'bold',
                    }}
                >
                    {value?.clusterName}
                </Typography>
                <Typography>({storeCount})</Typography>
                {value?.isDefault && (
                    <Tooltip title="Default cluster">
                        <VerifiedOutlined color="primary" fontSize={'small'} />
                    </Tooltip>
                )}
                <Typography
                    sx={{
                        fontStyle: 'italic',
                    }}
                >
                    {value?.description}
                </Typography>
            </Stack>
            <Stack direction="row" sx={{ ml: 'auto' }}>
                <Tooltip title={`Edit cluster ${value?.clusterName}`}>
                    <IconButton onClick={onEdit} size="small" color="info">
                        <Edit />
                    </IconButton>
                </Tooltip>

                <Tooltip
                    title={
                        value?.isDefault ? 'Default cluster cannot be deleted' : `Delete cluster ${value?.clusterName}`
                    }
                >
                    <span>
                        <IconButton disabled={value?.isDefault} onClick={onDelete} size="small" color="error">
                            <Delete />
                        </IconButton>
                    </span>
                </Tooltip>
            </Stack>
        </Stack>
    );
};
